/**
 * @file ProjectSidebar.js
 * @description Auto-maintained cleanup: comments & minor a11y/robustness. No UI/feature changes.
 */
import React, { useState, useEffect, useRef } from 'react';
import useClickOutside from './hooks/useClickOutside';
import './ProjectSidebar.css';
import CreditsWidget from './CreditsWidget';
import PlaygroundBlock from './PlaygroundBlock';
import SupportTicketModal from './SupportTicketModal';
import { exportProjectZip } from '../utilities/exportProject';
import { useOnboarding } from './OnboardingContext';

// === WORKFLOW PATCH: Use clear model options === (moved to constants/workflow.js)
import { WORKFLOW_STEPS } from './constants/workflow';

const defaultModelOptions = {
  text: [
    { alias: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro (default)', default: true },
    { alias: 'gpt-5.2', label: 'GPT‑5.2' },
  ],
  image: [
    { alias: 'gemini-3-pro-image-preview', label: 'Gemini 3 Pro Image (default)', default: true },
    { alias: 'gpt-image-1.5', label: 'GPT‑Image‑1.5' },
    { alias: 'dall-e-3', label: 'DALL·E 3' },
  ],
  video: [
    { alias: 'veo', label: 'Veo 3.1', default: true },
  ],
};

const STEP_TO_TAG_MAP = {
  screenplay: 'screenplay',
  characterBible: 'character',
  scenes: 'scene',
  videos: 'video',
  merge: 'video'
};

const ProjectSidebar = ({
  projects,
  onSelectProject,
  onGenerateScreenplay,
  onGenerateCharacterBible,
  onGenerateScenes,
  onCreateProject,
  selectedProject,
  logout, // Keep logout

  onRemoveProject,
  workflowState,
  setWorkflowState,
  onModelChange,
  onGenerateVideos,
  isScreenplayLoading,
  isCharacterBibleLoading,
  onMergeScenes, // Keep onMergeScenes
  isMerging,
  canMerge,
  onSelectArtifactType,
  artifactWorkbenchMode,
  onOpenAudioPlayground,
  // NEW: artifacts list for sidebar collapsibles (optional; safe if undefined)
  artifacts,
  // NEW: opens the generator dialog in a given mode
  onOpenArtifactGenerator,
  quickImages = [],
  quickVideos = [],
  quickAudio = [],
  onOpenImagePlayground = () => { },
  onOpenVideoPlayground = () => { },
  onOpenArtifact = null,

  modelOptions = defaultModelOptions,
  onDeleteArtifact = null, // (artifact, kind, projectId) => void
  user, // NEW: passed from MainPage
  isQuickMode = false, // NEW: fix visual lighting confusion
}) => {
  // State for managing UI interactions and local component state
  const [selectedProjectId, setSelectedProjectId] = useState(selectedProject ? selectedProject._id : null);
  const [contextMenu, setContextMenu] = useState(null);
  const [isScenesGenerating, setIsScenesGenerating] = useState(false);
  const [isVideosGenerating, setIsVideosGenerating] = useState(false);
  const { isActive, currentStep, nextStep, startTour } = useOnboarding();

  // Artifacts group expand/collapse
  const [isArtifactsExpanded, setIsArtifactsExpanded] = useState(false);
  const [openImages, setOpenImages] = useState(false);
  const [openVideos, setOpenVideos] = useState(false);
  const [openAudio, setOpenAudio] = useState(false);
  const [activeArtifactCat, setActiveArtifactCat] = useState(null);

  const [dots, setDots] = useState('');

  const [exportingId, setExportingId] = useState(null);
  const [activePlayground, setActivePlayground] = useState('image');
  const [artifactCtxMenu, setArtifactCtxMenu] = useState({ show: false, x: 0, y: 0, kind: null, artifact: null });

  // Ref for the context menu to handle clicks outside
  const contextMenuRef = useRef(null);

  // Support Modal State
  const [showSupportModal, setShowSupportModal] = useState(false);


  useEffect(() => {
    if (selectedProject && selectedProject._id) {
      setSelectedProjectId(selectedProject._id);
    } else {
      setSelectedProjectId(null);
    }
  }, [selectedProject]);

  useClickOutside(contextMenuRef, () => setContextMenu(null), !!contextMenu);
  // DOTS RUNNING
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 450);
    return () => clearInterval(interval);
  }, []);


  const handleModelChange = (projectId, stepKey, model) => {
    if (!setWorkflowState) return;
    setWorkflowState(prev => ({
      ...prev,
      [projectId]: {
        ...(prev?.[projectId] || {}),
        [stepKey]: {
          ...(prev?.[projectId]?.[stepKey] || {}),
          model,
        },
      },
    }));
    onModelChange && onModelChange(projectId, stepKey, model);
  };

  const handleSelectProject = (project) => {
    if (project._id === selectedProjectId) {
      setSelectedProjectId(null);
      onSelectProject(null);
    } else {
      setSelectedProjectId(project._id);
      onSelectProject(project);
    }
  };

  const handleCreateProject = () => {
    if (isActive && currentStep === 0) nextStep();
    setSelectedProjectId(null);
    onCreateProject();
  };

  const handleRightClick = (e, projectId) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4, projectId });
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleDeleteProject = async () => {
    if (contextMenu && contextMenu.projectId) {
      await onRemoveProject(contextMenu.projectId);
      handleCloseContextMenu();
      setSelectedProjectId(null);
      onSelectProject(null);
    }
  };

  const handleArtifactHeaderClick = (kind) => {
    setActiveArtifactCat(kind);               // highlight selected category
    onOpenArtifactGenerator && onOpenArtifactGenerator(null, kind); // your existing flow
  };

  const handleArtifactContextMenu = (e, kind, a) => {
    e.preventDefault();
    e.stopPropagation();
    setArtifactCtxMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      kind,
      artifact: a
    });
  };

  const closeArtifactCtxMenu = () => setArtifactCtxMenu({ show: false, x: 0, y: 0, kind: null, artifact: null });

  // Close artifact menu on global click... but we need a global listener or just leverage existing close logic?
  // Let's rely on useClickOutside or a simple overlay click
  useEffect(() => {
    const close = () => { if (artifactCtxMenu.show) closeArtifactCtxMenu(); };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [artifactCtxMenu.show]);

  const handleDownloadArtifact = async () => {
    const { artifact, kind } = artifactCtxMenu;
    closeArtifactCtxMenu();
    if (!artifact) return;

    const url = artifact.outputUrl || artifact.imageUrl || artifact.videoUrl || artifact.url || '';
    if (!url) return;

    const cleanTitle = (artifact.title || 'artifact').replace(/[^a-z0-9_-]/gi, '_');
    const ext = kind === 'image' ? 'png' : kind === 'video' ? 'mp4' : 'mp3';
    const filename = `${cleanTitle}.${ext}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      console.warn('Direct download failed, opening in new tab', e);
      window.open(url, '_blank');
    }
  };

  const handleDeleteArtifactClick = () => {
    const { artifact, kind } = artifactCtxMenu;
    closeArtifactCtxMenu();
    if (!artifact) return;
    if (onDeleteArtifact) {
      // Pass null for projectId override so MainPage uses logic (current or quick)
      onDeleteArtifact(artifact, kind, null);
    }
  };

  const allModelsIndex = React.useMemo(() => {
    const idx = new Map();
    for (const bucket of ['text', 'image', 'video']) {
      for (const m of (modelOptions?.[bucket] || [])) idx.set(m.alias, m);
    }
    return idx;
  }, [modelOptions]);

  const stepModelsFor = (stepKey, workflow) => {

    let bucket = (modelOptions?.text?.length) ? modelOptions.text : defaultModelOptions.text;

    if (stepKey === 'screenplay') {
      bucket = (modelOptions?.text?.length) ? modelOptions.text : defaultModelOptions.text;
    }

    if (stepKey === 'videos' || stepKey === 'merge') {
      bucket = (modelOptions?.video?.length) ? modelOptions.video : defaultModelOptions.video;
    }

    if (stepKey === 'scenes' || stepKey === 'characterBible') {
      bucket = (modelOptions?.image?.length) ? modelOptions.image : defaultModelOptions.image;
    }

    // Filter by tag if a tag requirement exists for this step
    const requiredTag = STEP_TO_TAG_MAP[stepKey];
    let options = bucket;

    if (requiredTag) {
      // Only keep models that have the required tag in their 'tags' array
      const filtered = bucket.filter(m => Array.isArray(m.tags) && m.tags.includes(requiredTag));
      // If we have matches, use them; otherwise fall back to the full bucket (backward compatibility)
      if (filtered.length > 0) {
        options = filtered;
      }
    }

    // Sort by sort_order if present
    options.sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));

    const legacyAlias = workflow?.[stepKey]?.model; // ensure `workflow` is in scope; else pass it in
    if (legacyAlias && !options.some(m => m.alias === legacyAlias)) {
      const legacy = allModelsIndex.get(legacyAlias) || { alias: legacyAlias, label: `${legacyAlias} (legacy)` };
      options = [...options, { ...legacy, deprecated: true }];
    }

    return options;
  };

  return (
    <div className="project-sidebar">
      <div className="header-container">
        <h3>Alekhine AI</h3>
        <button id="btn-create-new-project" className="create-project-button" onClick={handleCreateProject}>+</button>
      </div>
      <div className="ps-split">
        <div className="ps-top">
          <ul className="project-list">
            {projects.map((project) => {
              const projectWorkflow = workflowState?.[project._id];
              return (
                <li
                  key={project._id}
                  className={`project-item ${project._id === selectedProjectId ? 'selected' : ''}`}
                  onContextMenu={(e) => handleRightClick(e, project._id)}
                >
                  <div className="project-header">
                    {/* Project Title */}
                    <span
                      className="project-title"
                      onClick={() => handleSelectProject(project)}
                    >
                      {project.title}
                    </span>
                    {exportingId === project._id && (
                      <span className="project-downloading-indicator" title="Downloading...">
                        ⏳
                      </span>
                    )}
                  </div>
                  {/* --------- WORKFLOW PANEL --------- */}
                  {project._id === selectedProjectId && (
                    <div className="workflow-panel">
                      <ol className="workflow-steps">
                        {WORKFLOW_STEPS.map((step, idx) => {
                          const workflow = projectWorkflow || {};
                          // ---- NEW: screenplay fine-grained flags ----
                          const sc = workflow.screenplay || {};
                          const screenplayOutlineReady = !!(sc.outlineDone || sc.complete);
                          const screenplayDetailsReady = !!sc.detailsDone || !!sc.complete;

                          // ---- NEW: prev-step gating rules ----
                          const prevKey = idx > 0 ? WORKFLOW_STEPS[idx - 1].key : null;
                          let isPrevComplete = true;
                          if (idx > 0) {
                            if (step.key === 'characterBible' && prevKey === 'screenplay') {
                              // Character Bible unlocks after outline (not details)
                              isPrevComplete = screenplayOutlineReady;
                            } else if (prevKey === 'screenplay') {
                              // All other steps that follow screenplay require details to be done
                              isPrevComplete = screenplayDetailsReady;
                            } else {
                              isPrevComplete = !!(workflow[prevKey] && workflow[prevKey].complete);
                            }
                          }

                          // NEW: Scenes specifically requires screenplay details (text) to be ready
                          if (step.key === 'scenes') {
                            isPrevComplete = isPrevComplete && screenplayDetailsReady;
                          }

                          if (step.key === 'videos' && !isActive) {
                            isPrevComplete = false;
                          }
                          if (step.key === 'merge') {
                            isPrevComplete = isPrevComplete || canMerge;
                          }

                          // ---- NEW: screenplay complete means details are done ----
                          let isComplete = workflow[step.key]?.complete;
                          if (step.key === 'screenplay') {
                            isComplete = screenplayDetailsReady;
                          }

                          const stepModels = stepModelsFor(step.key, workflow);
                          const selectedModel =
                            workflow[step.key]?.model ||
                            (stepModels.find(m => m.default)?.alias || (stepModels[0]?.alias || ''));

                          // decide generating flags (unchanged)
                          let isGenerating = false;
                          if (step.key === 'screenplay') {
                            isGenerating = isScreenplayLoading || (screenplayOutlineReady && !screenplayDetailsReady);
                          }
                          else if (step.key === 'characterBible') isGenerating = isCharacterBibleLoading;
                          else if (step.key === 'scenes') isGenerating = isScenesGenerating;
                          else if (step.key === 'videos') isGenerating = isVideosGenerating;
                          else if (step.key === 'merge') isGenerating = isMerging;

                          // connect to proper generator (unchanged)
                          let onGenerateStep = null;
                          if (step.key === 'screenplay') onGenerateStep = onGenerateScreenplay;
                          else if (step.key === 'characterBible') onGenerateStep = onGenerateCharacterBible;
                          else if (step.key === 'scenes') onGenerateStep = onGenerateScenes;
                          else if (step.key === 'videos') onGenerateStep = onGenerateVideos;
                          else if (step.key === 'merge') onGenerateStep = onMergeScenes;

                          return (
                            <li
                              key={step.key}
                              className={`workflow-step ${isComplete ? 'step-complete' : ''} ${!isPrevComplete ? 'step-locked' : ''}`}
                            >
                              <span className="step-label">{step.label}</span>
                              <select
                                value={selectedModel}
                                onChange={(e) => handleModelChange(project._id, step.key, e.target.value)}
                                disabled={!isPrevComplete || isComplete}
                                className="model-dropdown"
                              >
                                {stepModels.map((m) => (
                                  <option key={m.alias} value={m.alias}>
                                    {m.label || m.alias}{m.deprecated ? ' — saved (legacy)' : ''}
                                  </option>
                                ))}
                              </select>

                              <button
                                id={step.key === 'merge' ? 'tour-merge' : `tour-generate-${step.key}`}
                                className={`workflow-generate-btn${isGenerating ? ' generating' : ''}`}
                                title={step.key === 'videos' && !isActive ? "Please generate videos through individual scenes until bulk generation is available" : ""}
                                disabled={
                                  !isPrevComplete ||
                                  isComplete ||
                                  isGenerating ||
                                  (step.key === 'videos' && !isActive) || // Locked: use individual scene video generation outside tutorial
                                  (step.key === 'merge' && !canMerge)
                                }
                                onClick={async () => {
                                  if (!onGenerateStep) return;
                                  try {
                                    if (step.key === 'scenes') setIsScenesGenerating(true);
                                    else if (step.key === 'videos') setIsVideosGenerating(true);
                                    await onGenerateStep(project, selectedModel);
                                  } finally {
                                    if (step.key === 'scenes') setIsScenesGenerating(false);
                                    else if (step.key === 'videos') setIsVideosGenerating(false);
                                  }
                                }}
                              >
                                {step.key === 'videos' && !isActive ? 'Coming Soon' : (isGenerating ? `Generating${dots.padEnd(3, ' ')}` : (isComplete ? '✓ Complete' : 'Generate'))}
                              </button>
                            </li>
                          );
                        })}

                      </ol>

                      {/* --------- ARTIFACTS SECTION --------- */}
                      <div className="artifacts-section">
                        <div className="artifacts-header-row">
                          <span className="section-label">Additional Media Artifacts</span>
                          <div className="artifacts-toggles">
                            <button
                              className={`artifact-toggle-btn ${artifactWorkbenchMode === 'image' && !isQuickMode ? 'active' : ''}`}
                              onClick={(e) => { e.stopPropagation(); handleArtifactHeaderClick('image'); }}
                              title="Image Artifacts"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                            </button>
                            <button
                              className={`artifact-toggle-btn ${artifactWorkbenchMode === 'video' && !isQuickMode ? 'active' : ''}`}
                              onClick={(e) => { e.stopPropagation(); handleArtifactHeaderClick('video'); }}
                              title="Video Artifacts"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                              </svg>
                            </button>
                            {/* Audio Placeholder */}
                            <button
                              className={`artifact-toggle-btn ${artifactWorkbenchMode === 'audio' && !isQuickMode ? 'active' : ''}`}
                              onClick={(e) => { e.stopPropagation(); handleArtifactHeaderClick('audio'); }}
                              title="Audio Artifacts"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18V5l12-2v13"></path>
                                <circle cx="6" cy="18" r="3"></circle>
                                <circle cx="18" cy="16" r="3"></circle>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* ---------------------------------- */}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

        </div>



        <div className="ps-bottom">
          {/* --- Toggle between Image and Video --- */}
          {/* --- Segmented Toggle (Icons) --- */}
          <div className="playground-toggle-bar segmented-toggle">
            <button
              className={`pg-toggle-btn ${activePlayground === 'image' ? 'active' : ''}`}
              onClick={() => {
                setActivePlayground('image');
                onOpenImagePlayground();
              }}
              title="Image Playground"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </button>
            <button
              className={`pg-toggle-btn ${activePlayground === 'video' ? 'active' : ''}`}
              onClick={() => {
                setActivePlayground('video');
                onOpenVideoPlayground();
              }}
              title="Video Playground"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
            </button>
            {/* Audio Playground (Global) */}
            <button
              className={`pg-toggle-btn ${activePlayground === 'audio' ? 'active' : ''}`}
              onClick={() => {
                setActivePlayground('audio');
                if (onOpenAudioPlayground) onOpenAudioPlayground();
              }}
              title="Audio Playground"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13"></path>
                <circle cx="6" cy="18" r="3"></circle>
                <circle cx="18" cy="16" r="3"></circle>
              </svg>
            </button>
          </div>

          {/* --- Image Playground --- */}
          {activePlayground === 'image' && (
            <PlaygroundBlock
              title="Image Playground"
              blockClassName="playground-images fixed-pos"
              items={quickImages}
              kind="image"
              onPlusClick={onOpenImagePlayground}
              onOpenArtifact={onOpenArtifact}
              onArtifactContextMenu={handleArtifactContextMenu}
            />
          )}

          {/* --- Video Playground --- */}
          {activePlayground === 'video' && (
            <PlaygroundBlock
              title="Video Playground"
              blockClassName="playground-videos fixed-pos"
              items={quickVideos}
              kind="video"
              onPlusClick={onOpenVideoPlayground}
              onOpenArtifact={onOpenArtifact}
              onArtifactContextMenu={handleArtifactContextMenu}
            />
          )}

          {/* --- Audio Playground --- */}
          {activePlayground === 'audio' && (
            <PlaygroundBlock
              title="Audio Playground"
              blockClassName="playground-audio fixed-pos"
              items={quickAudio || []} // quickAudio - will need to be passed from parent
              kind="audio"
              onPlusClick={onOpenAudioPlayground} // will need to be passed
              onOpenArtifact={onOpenArtifact}
              onArtifactContextMenu={handleArtifactContextMenu}
            />
          )}
        </div>
      </div>

      {/* Artifact Context Menu */}
      {artifactCtxMenu.show && (
        <div
          className="context-menu artifact-context-menu"
          style={{ top: artifactCtxMenu.y, left: artifactCtxMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ctx-header">
            {artifactCtxMenu.kind === 'image' ? 'Image' : artifactCtxMenu.kind === 'audio' ? 'Audio' : 'Video'} Options
          </div>
          <button onClick={handleDownloadArtifact}>Download</button>
          <button onClick={handleDeleteArtifactClick} className="danger">Delete</button>
        </div>
      )}

      {/* Project Context Menu */}
      {contextMenu && (
        <div
          className="context-menu artifact-context-menu"
          style={{ top: contextMenu.mouseY, left: contextMenu.mouseX }}
          onClick={handleCloseContextMenu}
          ref={contextMenuRef}
        >
          <div className="ctx-header">Project Options</div>
          <button onClick={async (e) => {
            e.stopPropagation();
            if (exportingId) return;
            // Use the projectId from contextMenu state
            const pid = contextMenu.projectId;
            const proj = projects.find(p => p._id === pid);
            if (!proj) return;

            handleCloseContextMenu();
            try {
              setExportingId(pid);
              await exportProjectZip(proj, {
                onProgress: () => { }
              });
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') console.error(err);
              alert('Export failed. Please try again.');
            } finally {
              setExportingId(null);
            }
          }}>
            {exportingId === contextMenu.projectId ? 'Downloading...' : 'Download'}
          </button>
          <button onClick={handleDeleteProject} className="danger">Delete</button>
        </div>
      )}

      <CreditsWidget />

      <div className="sidebar-footer">
        {user && user.email && (
          <>
            <button
              className="sidebar-help-icon"
              title="Help & Support"
              onClick={() => setShowSupportModal(true)}
            >
              ?
            </button>
            <div className="user-email" title={user.email}>
              {user.email}
            </div>
          </>
        )}
        <button className="logout-button" onClick={logout} title="Logout">
          ⏻
        </button>
      </div>

      {/* Support Ticket Modal */}
      {showSupportModal && (
        <SupportTicketModal
          user={user}
          onClose={() => setShowSupportModal(false)}
        />
      )}
    </div>
  );
};

export default React.memo(ProjectSidebar);
